import { _ } from "libs";

import React, { useEffect, FC } from "react";
import queryString from "query-string";

import withRedux from "../withRedux";

import ConversationsList from "../components/chat/conversationsList";
import ConversationDetail from "../components/chat/conversationDetail";

import { loadConversation, newConversation, closeComposer } from "../actions/chat";
import ConversationComposer from "../components/chat/conversationComposer";
import { Location, History } from "history";
import { Link } from "react-router-dom";
import Loader from "../components/loader";
import { Profile } from "../types";

type Props = {
	match: any;
	location: Location;
	history: History;
	loadConversation: typeof loadConversation;
	newConversation: typeof newConversation;
	closeComposer: typeof closeComposer;
	userId: string;
	loading: boolean;
	profile: Profile;
};

function parseRecipients(query: string): string[] {
	const parsed = queryString.parse(query, { arrayFormat: "comma" });

	if (Object.prototype.hasOwnProperty.call(parsed, "to")) {
		const to = parsed.to;
		if (!Array.isArray(to)) {
			return [to];
		} else {
			return to;
		}
	} else {
		return [];
	}
}

function parseType(query: string): string {
	const parsed = queryString.parse(query, { arrayFormat: "comma" });

	if (Object.prototype.hasOwnProperty.call(parsed, "type")) {
		return parsed.type as string;
	} else {
		return "";
	}
}

function parseParams(query: string): object {
	const parsed = queryString.parse(query, { arrayFormat: "comma" });

	if (Object.prototype.hasOwnProperty.call(parsed, "params")) {
		const stringified = parsed.params as string;
		return JSON.parse(stringified);
	} else {
		return {};
	}
}

const Messages: FC<Props> = ({
	match,
	loadConversation,
	location,
	history,
	closeComposer,
	newConversation,
	loading,
	userId,
	profile,
}: Props) => {
	useEffect(() => {
		if (match.params.hasOwnProperty("id")) {
			const id = match.params.id;
			loadConversation(id);
		}

		if (location.pathname === "/messages/new") {
			newConversation();
		}
	}, [match.params]);

	const onCreateConversation = (id: string) => {
		closeComposer();
		history.push(`/messages/conversation/${id}`);
	};

	const onConversationSelect = (id: string) => {
		const url = `/messages/conversation/${id}`;

		if (location.pathname.startsWith("/messages/new")) {
			history.push(url);
		} else {
			window.history.pushState({ clearEditor: true }, document.title, `${process.env.REACT_APP_BASE_PATH}${url}`);
		}
	};

	if (loading) {
		return <Loader />;
	}

	const composerOpen = location.pathname === "/messages/new" ? true : false;

	return (
		<div className="messagesPage">
			<div className="wrap">
				<div className="contentWrapper">
					<div className="contentLeft">
						<div className="conversations">
							<div className="conversationsHeader">
								<h5>{_("conversations.header")}</h5>
								<Link to={"/messages/new"} className="add" />
							</div>
							<ConversationsList onConversationSelect={onConversationSelect} userId={userId} />
						</div>
					</div>
					<div className="contentRight">
						{composerOpen ? (
							<ConversationComposer
								onCreateConversation={onCreateConversation}
								preload={parseRecipients(location.search)}
								userId={userId}
								defaultMessageType={parseType(location.search)}
								messageParams={parseParams(location.search)}
								profile={profile}
							/>
						) : (
							<ConversationDetail
								userId={userId}
								defaultMessageType={
									location.search.includes("type") ? parseType(location.search) : null
								}
								messageParams={parseParams(location.search)}
								profile={profile}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = ({ chat: { loading }, profile }, ownProps) => {
	return { loading, profile };
};

export default withRedux(
	{
		loadConversation,
		newConversation,
		closeComposer,
	},
	mapStateToProps
)(Messages);
